/**
 * Copyright (c) 2026 Spicy Friends Ltd.
 * All rights reserved.
 *
 * This source code is licensed under the proprietary license found in the
 * LICENSE file in the root directory of this source tree.
 */

'use client'

import {
  Button,
  Card,
  CardContent,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Label,
  Switch,
} from '@repo/shared-client/components/ui'
import { cn } from '@repo/shared-client/lib/utils'
import { Link } from '@tanstack/react-router'
import { initGA, logPageView, trackingScriptsNeedReloadOnConsentChange } from '@web/src/analytics'
import {
  getCookieConsent,
  hasStoredConsentChoice,
  setCookieConsent,
  subscribeOpenCookieSettings,
} from '@web/src/lib/cookie-consent/cookie-consent-store'
import { useTranslation } from '@web/src/lib/intl/react'
import { useEffect, useId, useState } from 'react'

const policyLinkClass = 'inline font-semibold text-primary underline-offset-4 hover:underline'
const BANNER_ENTER_DELAY_MS = 1000

const persistAndApplyAnalyticsConsent = (analytics: boolean) => {
  const next = { analytics }
  const needsReload = trackingScriptsNeedReloadOnConsentChange(next)
  setCookieConsent(next)

  if (needsReload) {
    window.location.reload()
    return
  }

  if (analytics) {
    initGA()
    logPageView()
  }
}

const CookieConsentBanner = () => {
  const { t } = useTranslation()
  const titleId = useId()
  const essentialId = useId()
  const analyticsId = useId()
  const [bannerVisible, setBannerVisible] = useState(false)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [analyticsEnabled, setAnalyticsEnabled] = useState(false)

  useEffect(() => {
    if (hasStoredConsentChoice()) {
      return
    }

    const timeoutId = window.setTimeout(() => {
      if (!hasStoredConsentChoice()) {
        setBannerVisible(true)
      }
    }, BANNER_ENTER_DELAY_MS)

    return () => {
      window.clearTimeout(timeoutId)
    }
  }, [])

  useEffect(() => {
    return subscribeOpenCookieSettings(() => {
      setAnalyticsEnabled(getCookieConsent().analytics)
      setDialogOpen(true)
    })
  }, [])

  const closeBanner = () => {
    setBannerVisible(false)
  }

  const handleAcceptAll = () => {
    persistAndApplyAnalyticsConsent(true)
    closeBanner()
    setDialogOpen(false)
  }

  const handleRejectNonEssential = () => {
    persistAndApplyAnalyticsConsent(false)
    closeBanner()
    setDialogOpen(false)
  }

  const handleOpenCustomize = () => {
    setAnalyticsEnabled(getCookieConsent().analytics)
    setDialogOpen(true)
  }

  const handleSavePreferences = () => {
    persistAndApplyAnalyticsConsent(analyticsEnabled)
    closeBanner()
    setDialogOpen(false)
  }

  const showBanner = bannerVisible && !dialogOpen

  return (
    <>
      {showBanner ? (
        <section
          aria-labelledby={titleId}
          className="fade-in slide-in-from-bottom fixed right-0 bottom-0 left-0 z-60 w-full max-w-xl animate-in duration-900 ease-out sm:bottom-4 sm:left-4"
        >
          <div className="relative overflow-visible">
            <div className="rounded-lg border border-border bg-muted p-4 shadow-2xl">
              <div className="flex flex-col gap-5">
                <div className="space-y-2">
                  <p className="font-semibold text-foreground text-md" id={titleId}>
                    {t('Cookie Policy')}
                  </p>
                  <p className="text-muted-foreground leading-relaxed">
                    {t(
                      'We use cookies and similar technologies to enhance your site experience and analyze performance. For details see our'
                    )}{' '}
                    <Link className={policyLinkClass} to="/helpcenter/legal/cookie-policy">
                      {t('Cookie Policy')}
                    </Link>
                  </p>
                </div>
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <button className={cn(policyLinkClass, 'text-md')} onClick={handleOpenCustomize} type="button">
                    {t('Manage preferences')}
                  </button>
                  <div className="grid grid-cols-2 gap-2 sm:w-56">
                    <Button onClick={handleRejectNonEssential} size="sm" type="button" variant="outline">
                      {t('Decline')}
                    </Button>
                    <Button onClick={handleAcceptAll} size="sm" type="button" variant="outline">
                      {t('Accept')}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      ) : null}

      <Dialog onOpenChange={setDialogOpen} open={dialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('Cookie settings')}</DialogTitle>
            <DialogDescription>
              {t('Choose which cookies to allow. Essential cookies are always on.')}{' '}
              <Link className={policyLinkClass} to="/helpcenter/legal/cookie-policy">
                {t('Cookie Policy')}
              </Link>
            </DialogDescription>
          </DialogHeader>

          <div className="flex flex-col gap-3">
            <Card size="sm">
              <CardContent className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <Label htmlFor={essentialId}>{t('Essential')}</Label>
                  <p className="mt-1 text-muted-foreground text-sm">
                    {t('Required for sign-in, language, security, and basic site features.')}
                  </p>
                </div>
                <Switch aria-label={t('Essential')} checked disabled id={essentialId} />
              </CardContent>
            </Card>

            <Card size="sm">
              <CardContent className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <Label htmlFor={analyticsId}>{t('Analytics')}</Label>
                  <p className="mt-1 text-muted-foreground text-sm">
                    {t(
                      'Helps us understand how the site is used. Includes Google Analytics and masked Sentry session replay.'
                    )}
                  </p>
                </div>
                <Switch
                  aria-label={t('Analytics')}
                  checked={analyticsEnabled}
                  id={analyticsId}
                  onCheckedChange={(checked) => {
                    setAnalyticsEnabled(checked === true)
                  }}
                />
              </CardContent>
            </Card>

            <p className="text-muted-foreground text-sm">
              {t('Changing analytics reloads the page so tracking and session replay can start or stop.')}
            </p>
          </div>

          <DialogFooter>
            <Button onClick={handleSavePreferences} type="button">
              {t('Save preferences')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}

export default CookieConsentBanner
